import {
    Component,
    ContentChildren,
    QueryList,
    Input,
    Output,
    EventEmitter,
    TemplateRef,
    AfterContentInit,
    ContentChild,
    AfterViewInit,
    ViewChild,
    ElementRef
} from '@angular/core';
import { ScrollPanelComponent } from '../../../Basic/components/ScrollPanel/scroll-panel.component';
import { TabComponent } from '../Tab/tab.component';
import { TabsStyleTypesEnum } from '../../enums/tabs-types.enum';
import { ActionKeys } from '../../../Forms/enums/keys.enum';
import { ButtonTypesEnum } from '../../../Forms/enums/button-types.enum';

export enum TabsOrientationsEnum {
    VERTICAL = 'vertical',
    HORIZONTAL = 'horizontal'
}

@Component({
    selector: 'agpr-tabs',
    templateUrl: './tabs.component.html',
    styleUrls: ['./tabs.component.scss'],
    standalone: false
})
export class TabsComponent implements AfterContentInit, AfterViewInit {
    readonly buttonTypes = ButtonTypesEnum;
    _selectedTabIndex: number = null;

    // This variable is only used for keyboard accessibility
    visibleTabs: TabComponent[];

    initialTabIndex: number;
    contentInitialized: boolean = false;

    @Input() set selectedTabIndex(value: number) {
        if (!this.contentInitialized) {
            this.initialTabIndex = value;

            return;
        }

        if (value !== this._selectedTabIndex) {
            this.selectTabByIndex(value);
        }
    }

    @Input() style: TabsStyleTypesEnum = TabsStyleTypesEnum.default;
    @Input() tabsHeadingLevel: number = 3;
    @Input() orientation: TabsOrientationsEnum = TabsOrientationsEnum.HORIZONTAL;
    @Input() useContentContainer: boolean = true;
    @Input() variableHeaderWidth: boolean = false;
    @Input() addNewTab: string; // it's also the tooltip
    @Input() addNewDisabled: boolean = false;
    @Input() tabLineWidth: number = 20;
    @Output() selectedTabIndexChange: EventEmitter<number> = new EventEmitter<number>();
    @Output() tabChange: EventEmitter<TabComponent> = new EventEmitter<TabComponent>();
    @Output() addNewTrigger: EventEmitter<void> = new EventEmitter<void>();

    tabsStyleTypesEnum = TabsStyleTypesEnum;
    tabHeader: TemplateRef<any>;
    actionKeysEnum = ActionKeys;
    unfocusedTabs: any;

    @ViewChild('scrollPanel') scrollPanel: ScrollPanelComponent;
    @ContentChildren(TabComponent) tabs: QueryList<TabComponent>;

    @ContentChild('tabTemplate') public tabTemplate: TemplateRef<any>;

    @ViewChild('tabLine') public tabLine: ElementRef;

    @ContentChild('tabActions', { read: ElementRef }) tabActionsElement?: ElementRef;

    selectedTabResizeObserver: ResizeObserver = new ResizeObserver((entries) => {
        entries.forEach(entry => {
            this.setTabLineWidth(entry.target as HTMLElement);
        });
    });

    constructor(protected el: ElementRef) {}

    ngAfterContentInit(): void {
        this.contentInitialized = true;
        requestAnimationFrame(() => {
            this.selectTabByIndex(this.initialTabIndex || 0, false, false);
        });
    }

    ngAfterViewInit(): boolean {
        const tab = this.tabs.toArray()[this._selectedTabIndex];

        if (tab && this.tabHeader !== tab.header) {
            // re-select the tab if the view initialized a header
            // has to be a separate frame to make sure it works outside of dev mode
            requestAnimationFrame(() => {
                this.selectTab(tab, false);
                this.addFocus(tab, false);
            });

            return true;
        }

        this.removeFocus();

        return false;
    }

    selectTabByIndex(index: number, emitChanges: boolean = true, moveFocus: boolean = true): void {
        if (!this.tabs) {
            return;
        }
        this.selectTab(this.tabs.toArray()[index] || this.tabs.first, emitChanges, moveFocus);
        this.addFocus(this.tabs.toArray()[index], moveFocus);
    }

    selectTab(selectedTab: TabComponent, emitChanges: boolean = true, moveFocus: boolean = true): void {
        if (!selectedTab) {
            this.unselectTab();
            return;
        }
        if (selectedTab.disabled) {
            return;
        }

        let index = 0;

        this.tabs.forEach((tab, i) => {
            tab.selected = tab === selectedTab;
            if (tab.selected) {
                index = i;
            }
        });

        this._selectedTabIndex = index;
        this.initialTabIndex = null;
        this.scrollPanel?.scrollTo(0, 0, false);
        if (emitChanges) {
            this.selectedTabIndexChange.emit(index);
        }

        if (this.tabs.length) {
            this.tabChange.emit(this.tabs.toArray()[index]);
        }

        this.tabHeader = selectedTab.header;

        if (this.tabLine?.nativeElement) {
            requestAnimationFrame(() => {
                const tabElement = this.el.nativeElement.querySelector('#' + selectedTab.id.replace(/\./g, '\\.')) as HTMLElement;
                if (this.variableHeaderWidth) {
                    this.selectedTabResizeObserver.disconnect();
                    this.selectedTabResizeObserver.observe(tabElement);
                } else {
                    this.setTabLineWidth(tabElement);
                }

            });
        }

        this.addFocus(selectedTab, moveFocus);
    }

    removeFocus(): void {
        this.unfocusedTabs = Array.from(this.el.nativeElement.querySelectorAll('[role=tab]'));

        for (let i = 0; i < this.unfocusedTabs.length; i++) {
            const unfocusedTab = this.unfocusedTabs[i];
            unfocusedTab.tabIndex = -1;
        }
    }

    addFocus(selectedTab: TabComponent, moveFocus: boolean = true): void {
        this.removeFocus();
        if (!selectedTab) {
            return;
        }

        selectedTab.focused = true;
        const selectedTabElement = this.el.nativeElement.querySelector('#' + selectedTab?.id?.replace(/\./g, '\\.')) as HTMLElement;
        selectedTabElement?.setAttribute('tabindex', '0');
        if (moveFocus) {
            selectedTabElement?.focus();
        }
    }

    public unselectTab(): void {
        if (!this.tabs) {
            return;
        }
        this.tabs.forEach(t => t.selected = false);
        this._selectedTabIndex = null;
        this.selectedTabIndexChange.emit(null);
    }

    /**
    * This function is only used for keyboard accessibility
    * it is called whenever the user presses the arrow keys, enter & space
    */

    onKeyUp(event: KeyboardEvent): void {
        switch (event.key) {
            case this.actionKeysEnum.RIGHT:
            case this.actionKeysEnum.DOWN:
                if (this.isKeyOrientationMatching(event)) {
                    this.removeFocus();
                    if (this.tabs.toArray()[this._selectedTabIndex + 1]?.disabled) {

                        if ((this._selectedTabIndex + 1) === this.tabs.length) {
                            this.selectTab(this.tabs.first);
                        } else {
                            this._selectedTabIndex += 2;
                        }
                    } else {
                        this._selectedTabIndex += 1;
                    }
                    this.selectTabByIndex(this._selectedTabIndex);
                }
                break;
            case this.actionKeysEnum.LEFT:
            case this.actionKeysEnum.UP:
                if (this.isKeyOrientationMatching(event)) {
                    this.removeFocus();
                    if (this.tabs.toArray()[this._selectedTabIndex - 1]?.disabled) {
                        this._selectedTabIndex -= 2;
                    } else {
                        this._selectedTabIndex -= 1;
                    }

                    if (this._selectedTabIndex === -1) {
                        this.selectTab(this.tabs.last);
                    } else {
                        this.selectTabByIndex(this._selectedTabIndex);
                    }
                }
                break;
        }
    }

    protected isKeyOrientationMatching(event: KeyboardEvent): boolean {
        return this.orientation === TabsOrientationsEnum.HORIZONTAL ?
            (event.key === ActionKeys.LEFT || event.key === ActionKeys.RIGHT)
            : (event.key === ActionKeys.UP || event.key === ActionKeys.DOWN);
    }

    setTabLineWidth(tabElement: HTMLElement) {
        this.tabLine.nativeElement.style.width = tabElement.clientWidth - this.tabLineWidth + 'px';
        const translateValue = tabElement.offsetLeft - 10;
        this.tabLine.nativeElement.style.transform = 'translateX(' + translateValue + 'px)';
    }
}
